// Declining to start a trace read, for the traces a reader passes through.
//
// Withdrawing a read stops the ANSWER, not the scan: the query runs under
// `wait_end_of_query`, so the server buffers the whole result and runs to
// `max_execution_time` however quickly the reader moves on. traceReadGate bounds
// how fast one tab can CREATE those scans and nothing on this side of the wire
// can make an issued one cheaper — that needs a `KILL QUERY`. What a client can
// do is not issue it: a trace left inside the dwell costs a timer, which is the
// only abandoned read that is free.
//
// HOW LONG. The interval is bounded from both ends by how the panel already
// behaves, rather than picked round:
//
//   Below — a pass-through visit has to fall inside it. A held arrow key
//   delivers a trace every few tens of milliseconds and a deliberate
//   click-and-move-on a few hundred, so a dwell under ~250ms still pays in full
//   for the skim that is the whole case worth catching.
//
//   Above — what the dwell delays is a SECOND layer. The turn reading renders
//   from the body already in hand; the capture instants and the endpoint marks
//   cannot appear until the priors land AND a probe resolves against them, which
//   is two sequential round trips at best. The dwell is a fraction added to a
//   wait already measured in round trips, not to a blank panel and not to a
//   frame budget.
//
//   And this dashboard has already priced the same trade for values that cross
//   the network: useDebouncedValue's call sites hold 300ms (inventory search),
//   350ms (pipeline query) and 500ms (steer, kernel preview) before firing,
//   each holding back a PRIMARY result. 400 sits inside that band, for a
//   secondary one.
//
// The dwell CANNOT cut coverage, and that is a property of where it sits rather
// than a promise: it delays the start of the reading and changes nothing about
// it. A reader who stays gets every window they get today, marginally later.
//
// A HIDDEN TAB never settles. "The reader stayed" is a claim about attention,
// and a backgrounded tab is the one case where the timer would be measuring
// nobody's. The timer only runs while the document is visible and restarts when
// it comes forward — restarts rather than resumes, because background timer
// throttling means a dwell that elapsed while hidden measured nothing anyway.

import { useEffect, useState } from "react";

export const TRACE_READ_DWELL_MS = 400;

let dwellMs = TRACE_READ_DWELL_MS;

const documentOrNull = () => (typeof document === "undefined" ? null : document);

// The starting value, and deliberately not null: a caller whose key is null
// until its data arrives would otherwise read as already settled on the first
// render, which is the one render where nothing can have elapsed.
const NOTHING = Symbol("trace read dwell: nothing has elapsed");

/**
 * Whether the reader has settled on `key` long enough for its reads to start.
 *
 * @param key      the identity of the thing being read — the caller's existing
 *   one (`focalKey` / `idKey`), never a fresh object. A re-render that does not
 *   change the key does not re-arm: the timer is armed by the key, so an elapsed
 *   dwell stays elapsed however many times the caller re-renders under it.
 * @param enabled  false arms nothing, for a caller that could not read anyway.
 * @returns false until the dwell has elapsed for THIS key, true after. A timer
 *   that fires late can only ever settle the key it was armed for, so a rapid
 *   switch cannot start the next trace's reading early.
 */
export function useTraceReadDwell(key, enabled = true) {
  // The key the dwell has elapsed for, not a flag: compared against the CURRENT
  // key on every render, so the answer is about the trace on screen and a stale
  // timer has nothing it can turn on. Remembering the last one also means a
  // reader flipping back to a trace they already committed to does not pay the
  // dwell again.
  const [elapsedKey, setElapsedKey] = useState(NOTHING);

  useEffect(() => {
    if (!enabled || elapsedKey === key) return undefined;
    const doc = documentOrNull();
    let timer = null;
    const stop = () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    };
    const start = () => {
      stop();
      if (doc?.visibilityState === "hidden") return;
      timer = setTimeout(() => {
        timer = null;
        setElapsedKey(key);
      }, dwellMs);
    };
    start();
    doc?.addEventListener("visibilitychange", start);
    return () => {
      stop();
      doc?.removeEventListener("visibilitychange", start);
    };
  }, [key, enabled, elapsedKey]);

  return elapsedKey === key;
}

/**
 * Drive the dwell from a test instead of waiting it out. Called with nothing —
 * or anything not a number — restores the shipped interval, which every suite
 * that touches it must do, since the module is shared across a whole run.
 */
export function _setTraceReadDwellForTests(ms) {
  dwellMs = Number.isFinite(ms) ? Math.max(0, ms) : TRACE_READ_DWELL_MS;
}
